import {get, set} from 'lodash';
import {Injectable} from '@angular/core';
import {Http, Response, ResponseContentType, URLSearchParams} from '@angular/http';
import {Observable} from 'rxjs';
import {Money} from '../../../../../common/api';
import {
    ApprovedTransfersSummaryPagination,
    CheckAddressesResponse,
    FileSummary,
    OutgoingTransfer,
    OutgoingTransferPagination,
    TransferAttachment,
    UploadedTransfersSummaryPagination
} from '../entity/transfer';

@Injectable()
export class TransfersService {

    constructor(private _http: Http) {
    }

    exportTransfers(liquidityAccountNumber: string, transfers: number): Observable<any> {
        return this._http.post(`/api/backoffice/transfers/outgoing/export-batches/${liquidityAccountNumber}?export`, {transfers});
    }

    getUploadedOutgoingTransfersSummary(page: number, limit: number): Observable<UploadedTransfersSummaryPagination> {
        let search = this.createPageSearchParams(page, limit);
        return this._http.get('/api/backoffice/transfers/outgoing/export-files', {search: search})
        .map(response => response.json())
        .map(response => ({
            page: get(response, 'meta.page', 0),
            records: get(response, 'meta.records', 0),
            total_records: get(response, 'meta.total_records'),
            poolSummaries: get(response, 'poolSummaries', []).map((poolSummary) => ({
                id: poolSummary.id,
                name: poolSummary.name,
                liquidityAccountSummaries: get(poolSummary, 'liquidityAccounts', []).map((liquidityAccountSummary) => ({
                    id: liquidityAccountSummary.id,
                    name: liquidityAccountSummary.name,
                    fileSummaries: get(liquidityAccountSummary, 'fileSummaries', []).map((fileSummary) => ({
                        transfers: fileSummary.transfers,
                        totalAmount: fileSummary.totalAmount,
                        period: fileSummary.period,
                        fileId: fileSummary.fileId,
                        fileName: fileSummary.fileName,
                        state: fileSummary.state,
                        canDone: fileSummary.canDone
                    }))
                }))
            }))
        }));
    }

    getApprovedOutgoingTransfersPoolSummaries(page: number, limit: number): Observable<ApprovedTransfersSummaryPagination> {
        let search = this.createPageSearchParams(page, limit);
        return this._http.get('/api/backoffice/transfers/outgoing/export-batches', {search: search})
        .map(response => response.json())
        .map(response => ({
            page: get(response, 'meta.page', 0),
            records: get(response, 'meta.records', 0),
            total_records: get(response, 'meta.total_records'),
            poolSummaries: get(response, 'poolSummaries', []).map((poolSummary) => ({
                id: poolSummary.id,
                name: poolSummary.name,
                liquidityAccountSummaries: get(poolSummary, 'liquidityAccounts', []).map((liquidityAccountSummary) => ({
                    id: liquidityAccountSummary.id,
                    name: liquidityAccountSummary.name,
                    number: liquidityAccountSummary.number,
                    transfers: liquidityAccountSummary.transfers,
                    totalAmount: liquidityAccountSummary.totalAmount,
                    period: liquidityAccountSummary.period
                }))
            }))
        }));
    }

    getUploadedOutgoingTransfers(fileId: number, page: number, limit: number): Observable<OutgoingTransferPagination> {
        let search = this.createPageSearchParams(page, limit);
        return this._http.get(`/api/backoffice/transfers/outgoing/export-files/${fileId}`, {search: search})
        .map(response => response.json())
        .map(response => ({
            page: get(response, 'meta.page', 0),
            records: get(response, 'meta.records', 0),
            total_records: get(response, 'meta.total_records', 0),
            liquidityAccountName: get(response, 'liquidityAccountName', ''),
            fileName: get(response, 'fileName', ''),
            transfers: get(response, 'transfers', []).map(transfer => {
                return this.mapToUploadedTransfer(transfer);
            })
        }));
    }

    getCreatedOutgoingTransfers(page: number, limit: number): Observable<OutgoingTransferPagination> {
        return this._http.get('/api/backoffice/transfers/outgoing/authorized', {search: this.createPageSearchParams(page, limit)})
        .map(response => response.json())
        .map(response => ({
            page: get(response, 'meta.page', 0),
            records: get(response, 'meta.records', 0),
            total_records: get(response, 'meta.total_records', 0),
            transfers: get(response, 'transfers', []).map(transfer => {
                return this.mapTransfer(transfer);
            })
        }));
    }

    getApprovedOutgoingTransfersByLiquidityAccountNumber(liquidityAccountNumber: number, page: number, limit: number): Observable<OutgoingTransferPagination> {
        let search = this.createPageSearchParams(page, limit);
        return this._http.get(`/api/backoffice/transfers/outgoing/export-batches/${liquidityAccountNumber}`, {search: search})
        .map(response => response.json())
        .map(response => ({
            page: get(response, 'meta.page', 0),
            records: get(response, 'meta.records', 0),
            total_records: get(response, 'meta.total_records', 0),
            liquidityAccountName: get(response, 'liquidityAccountName', ''),
            transfers: get(response, 'transfers', []).map(transfer => {
                return this.mapTransfer(transfer);
            })
        }));
    }

    markTransfersAsUploaded(fileId: number): Observable<FileSummary> {
        return this._http.patch(`/api/backoffice/transfers/outgoing/export-files/${fileId}?upload`, {})
        .map(response => response.json());
    }

    markFileAsDone(fileId: number): Observable<any> {
        return this._http.patch(`/api/backoffice/transfers/outgoing/export-files/${fileId}?done`, {})
        .map(response => response.json());
    }

    rejectTransfer(paymentId: number, reason: string): Observable<Response> {
        return this._http.put(`/api/backoffice/transfers/${paymentId}/reject`, {reason: reason});
    }

    approveTransfer(paymentId: number, reason: string): Observable<Response> {
        return this._http.put(`/api/backoffice/transfers/${paymentId}/approve`, {reason: reason});
    }

    saveTransferReference(paymentId: number, oldReference: string, newReference: string) {
        return this._http.patch(`/api/backoffice/transfers/outgoing/${paymentId}/reference`, {
            current_reference: oldReference,
            new_reference: newReference
        }).map((resp) => resp.json());
    }

    saveBankFee(paymentId: number, newBankFee: Money) {
        return this._http
        .patch(`/api/backoffice/transfers/outgoing/${paymentId}/bank-fee`, {amount: newBankFee})
        .map(resp => resp.json());
    }

    confirm(paymentId: number, reference: string) {
        let search: URLSearchParams = new URLSearchParams();
        search.set('reference', reference);

        return this._http
        .put(`/api/backoffice/transfers/${paymentId}/confirm`, {}, {search: search})
        .map((resp) => resp.json());
    }

    getTransferAttachments(transferID: number): Observable<TransferAttachment[]> {
        return this._http
        .get(`/api/backoffice/transfers/${transferID}/attachments`)
        .map(response => response.json())
        .map(response => response.records);
    }

    downloadTransferAttachment(attachmentID: number): Observable<Response> {
        return this._http.get(`/api/backoffice/documents/${attachmentID}/download`,
            {responseType: ResponseContentType.Blob})
    }

    downloadTransferAttachmentsAsZip(transferId: number): Observable<Response> {
        return this._http.get(`/api/backoffice/documents/${transferId}/download/all`,
            {responseType: ResponseContentType.Blob})
    }

    checkAddresses(transferId: number): Observable<CheckAddressesResponse> {
        return this._http.get(`/api/backoffice/transfers/outgoing/${transferId}/check-addresses`)
        .map(response => response.json());
    }

    private mapTransfer(transfer): OutgoingTransfer {
        return {
            payment: {
                id: transfer.payment.id,
                date: transfer.payment.date,
                time: transfer.payment.time,
                narrative: transfer.payment.narrative,
                state: transfer.payment.state,
                amount: {
                    amount: transfer.payment.amount.amount,
                    currency_code: transfer.payment.amount.currency_code,
                },
                isExchange: transfer.payment.isExchange,
                bankFeeAmount: {
                    amount: transfer.payment.bankFeeAmount.amount,
                    currency_code: transfer.payment.bankFeeAmount.currency_code,
                },
                isExpress: transfer.payment.isExpress
            },
            customer: {
                id: transfer.customer.id,
                name: transfer.customer.name,
                accountNumber: transfer.customer.accountNumber,
                accountName: transfer.customer.accountName,
                riskLevel: {
                    id: transfer.customer.riskLevel.id,
                    name: transfer.customer.riskLevel.name,
                    level: transfer.customer.riskLevel.level,
                    hexColor: transfer.customer.riskLevel.hexColor
                }
            },
            beneficiary: {
                account: transfer.beneficiary.account,
                name: transfer.beneficiary.name,
                bank: {
                    name: transfer.beneficiary.bank.name,
                    country: {
                        id: transfer.beneficiary.bank.country.id,
                        name: transfer.beneficiary.bank.country.name,
                        abbreviation: transfer.beneficiary.bank.country.abbreviation
                    }
                }
            },
            account: {
                id: transfer.account.id,
                name: transfer.account.name,
                number: transfer.account.number,
                balance: {
                    amount: transfer.account.balance.amount,
                    currency_code: transfer.account.balance.currency_code,
                },
                iban: transfer.account.iban,
                refill_date: transfer.account.refill_date,
                bank_country: transfer.account.bank_country,
                iban_locator: transfer.account.iban_locator
            },
            reason: transfer.reason,
            approvedReason: transfer.approvedReason
        }
    }

    private mapToUploadedTransfer(uploadedTransfer): OutgoingTransfer {
        let transfer: OutgoingTransfer = this.mapTransfer(uploadedTransfer);
        set(transfer, 'payment.reference', uploadedTransfer.payment.reference);
        return transfer;
    }

    private createPageSearchParams(page: number, limit: number): URLSearchParams {
        let search: URLSearchParams = new URLSearchParams();
        search.set('page', String(page));
        search.set('limit', String(limit));
        return search;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/api/service/transfers.service.ts